<template>
  <Loading v-if="state == 'loading'"></Loading>

  <div v-if="state == 'idle'">
    <div class="flex justify-between px-4 py-5 shadow bg-slate-300 rounded-b-3xl">
      <div>
        <router-link :to="{name: 'Profile'}">
          <p class="mt-2 text-2xl leading-none">{{ data.user.name }}</p>
          <p class="text-lg leading-none text-gray-600">#{{ data.user.id }}</p>
          <p class="mt-2 text-gray-600">{{ data.user.type }}</p>
        </router-link>

        <div>
          <p v-if="!data.user.latitude || !data.user.longitude" @click="$router.push({name: 'ProfileMap'})" class="px-2 py-1 mt-2 text-sm leading-none text-gray-600 uppercase bg-red-200 rounded-sm cursor-pointer font-catamaran max-w-max">Set Location</p>
          <p v-if="$store.state.appVersion != data.latestAppVersion" @click="openPlaystore()" class="px-2 py-1 mt-2 text-sm leading-none text-gray-600 uppercase bg-green-200 rounded-sm cursor-pointer font-catamaran max-w-max">Update Available</p>
        </div>
      </div>

      <div class="text-right">
        <router-link :to="{name: 'Notifications'}" class="relative">
          <i class="text-2xl ion-md-notifications-outline"></i>
          <span v-if="data.count_notifications != 0" class="absolute top-0 right-0 px-2 py-0 -mt-1 -mr-2 leading-none bg-red-300 rounded-full">{{ data.count_notifications }}</span>
        </router-link>

        <router-link :to="{name: 'Transactions'}">
          <p class="mt-4 text-2xl leading-none"><small class="text-base">₹</small>{{ data.user.cash_balance }} <i class="text-base ion-ios-wallet"></i></p>
          <p class="leading-none text-gray-600">Balance</p>
        </router-link>
      </div>
    </div>

    <div class="p-4">
      <div v-if="data.hasAnnouncement">
        <p class="mt-1 text-xl text-gray-500">Announcement</p>
        <div @click="openAnnouncement(data.announcement.url)" class="block px-3 py-2 mt-1 mb-5 bg-white shadow rounded-2xl">
          <p class="text-lg text-gray-900">{{ data.announcement.title }}</p>
          <p class="text-sm text-gray-600">{{ data.announcement.message }}</p>
        </div>
      </div>

      <p class="text-xl text-gray-500">Statistics</p>
      <div class="mt-2 grid grid-cols-6 gap-x-2 gap-y-3">
        <router-link :to="{name: 'Customers'}" class="inline-block px-3 py-2 bg-white shadow col-span-2 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Customers</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_customers }}</p>
        </router-link>

        <router-link :to="{name: 'Subscriptions'}" class="inline-block px-3 py-2 bg-white shadow col-span-2 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Subs</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_subscriptions_active }}</p>
        </router-link>

        <router-link :to="{name: 'Vacations'}" class="inline-block px-3 py-2 bg-white shadow col-span-2 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Vacations</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_vacations }}</p>
        </router-link>

        <router-link :to="{name: 'Orders'}" class="inline-block px-3 py-2 bg-white shadow col-span-3 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Orders</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_orders }}</p>
        </router-link>

        <router-link :to="{name: 'Leads'}" class="inline-block px-3 py-2 bg-white shadow col-span-3 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Samples</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_leads }}</p>
        </router-link>

        <router-link :to="{name: 'Groups'}" class="inline-block px-3 py-2 bg-white shadow col-span-3 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Groups</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_groups }}</p>
        </router-link>

        <router-link :to="{name: 'DeliveryPersons'}" class="inline-block px-3 py-2 bg-white shadow col-span-3 rounded-2xl">
          <p class="text-lg leading-none text-gray-500">Delivery Persons</p>
          <p class="mt-1 text-3xl leading-none">{{ data.count_delivery_persons }}</p>
        </router-link>
      </div>

      <router-link :to="{name: 'Deliveries', params: {date: getToday()}}" class="block px-3 py-2 mt-3 bg-white shadow rounded-2xl">
        <div class="flex justify-between">
          <p class="text-lg leading-none text-gray-500">Deliveries</p>
          <span v-if="data.day_delivery == 'Today'" class="px-2 py-1 text-xs leading-none text-gray-600 uppercase bg-green-200 rounded-sm font-catamaran">Today</span>
          <span v-if="data.day_delivery == 'Tomorrow'" class="px-2 py-1 text-xs leading-none text-gray-600 uppercase bg-red-200 rounded-sm font-catamaran">Tomorrow</span>
        </div>
        <div class="flex justify-between mt-3 text-3xl leading-none">
          <div>
            <p class="text-lg leading-none text-gray-600">Total</p>
            <p class="leading-none">{{ data.count_deliveries }}</p>
          </div>
          <div>
            <p class="text-lg leading-none text-gray-600">1 Litre</p>
            <p class="leading-none">{{ data.count_deliveries_1l }}</p>
          </div>
          <div>
            <p class="text-lg leading-none text-gray-600">500 ML</p>
            <p class="leading-none">{{ data.count_deliveries_500ml }}</p>
          </div>
        </div>
      </router-link>

      <DeliveryIntent :intent="data.delivery_intent" :manualCount="data.user.manual_count" :editManual="data.edit_manual" :deliveries="data"/>

      <div class="mt-5">
        <p class="text-xl text-gray-500">Actions</p>
        <div class="flex flex-col mt-2 text-gray-600 bg-white shadow divide-slate-100 divide-y-2 rounded-2xl">
          <router-link :to="{name: 'Reports'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-ios-document"></i>
            <span>Reports</span>
          </router-link>
          <router-link :to="{name: 'CreateCustomer'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-md-person"></i>
            <span>Create Customer</span>
          </router-link>
          <router-link :to="{name: 'CreateTransaction'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-md-cash"></i>
            <span>Create Transaction</span>
          </router-link>
          <router-link :to="{name: 'CreateSubscription'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-md-refresh"></i>
            <span>Create Subscription</span>
          </router-link>
          <router-link :to="{name: 'CreateVacation'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-md-airplane"></i>
            <span>Create Vacation</span>
          </router-link>
          <router-link :to="{name: 'ShopLogsIndex'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 icon ion-md-image"></i>
            <span>Create Shop Logs</span>
          </router-link>
          <router-link :to="{name: 'CreateNotification'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-md-notifications"></i>
            <span>Create Notification</span>
          </router-link>
          <router-link :to="{name: 'TicketsIndex'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 ion-md-chatbubbles"></i>
            <div class="relative flex items-center justify-start gap-x-1">
              <p>Tickets</p>
              <span v-if="data.openTickets != 0" class="px-2 py-0 leading-none text-white bg-red-400 rounded-full">{{ data.openTickets }}</span>
            </div>
          </router-link>
          <router-link :to="{name: 'BottleExchangeIndex'}" class="flex justify-start px-3 py-3 text-lg leading-none">
            <i class="w-8 ml-2 icon ion-md-swap"></i>
            <span>Bottle Exchange</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import Loading from '@/components/common/Loading.vue';
import DeliveryIntent from '@/components/dashboard/DeliveryIntent.vue';
import mixin from '@/utils/mixin.js';
import dayjs from 'dayjs';

export default {
  name: 'Dashboard',
  mixins: [ mixin ],
  components: { Loading, DeliveryIntent },
  data() {
    return {
      state: 'loading',
      data: {},
    }
  },

  methods: {
    openPlaystore() {
      window.open(this.$store.state.playstoreLink, '_system');
    },

    openAnnouncement(url = null) {
      if (url != null) {
        if (url.startsWith('http')) window.open(url, '_system');
        else this.$router.push({name: url});
      }
    },

    getToday() {
      if (dayjs().hour() >= 19 && dayjs().minute() >= 1) {
        return dayjs().add(1, 'day').format('YYYY-MM-DD');
      }

      return dayjs().format('YYYY-MM-DD');
    },

    getUserData() {
      this.state = 'loading';
      this.request('GET', 'userInfo/' + this.$store.state.appVersion, {}, (resp) => {
        this.state = 'idle';

        if (resp.success) {
          this.data = resp.data;
          this.$store.state.user = resp.data.user;
          window.localStorage.setItem('dealer_user', JSON.stringify(resp.data.user));

          if (!window.localStorage.getItem('dealer_logins')) {
            window.localStorage.setItem('dealer_logins', JSON.stringify([{id: this.data.user.id, name: this.data.user.name, api_token: this.data.user.api_token}]));
          }

          this.updateDeviceinfo();
          if (this.$store.state.platform != 'web') this.checkFcmToken();
        } else {
          if (resp.message == 'Unauthenticated.') this.logout();
          else alert(resp.message ? resp.message : 'Unable to fetch details, please try again.');
        }
      });
    },

    updateDeviceinfo() {
      if (!window.sessionStorage.getItem('deviceInfoUpdated')) {
        let logins = JSON.parse(window.localStorage.getItem('dealer_logins')).map(login => login.id);

        Device
          .getInfo()
          .then(e => this.request('POST', 'updateDevice', {device_info: e, logins: logins}, () => {
            window.sessionStorage.setItem('deviceInfoUpdated', true);
          }));
      }
    },
  },

  created() {
    if (!this.$store.state.api_token) this.$router.push('/');
    else this.getUserData();

    if (this.$store.state.user.name != null) this.data.user = this.$store.state.user;

    App.addListener('backButton', () => App.exitApp());
  },

  unmounted() {
    App.removeAllListeners();
  },
}
</script>
